import React, {
  useState, useCallback, useEffect, useMemo, useRef,
} from 'react';
import { Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import DeviceList from './DeviceList';
import BottomMenu from '../common/components/BottomMenu';
import StatusCard from '../common/components/StatusCard';
import { devicesActions, sessionActions } from '../store';
import usePersistedState from '../common/util/usePersistedState';
import EventsDrawer from './EventsDrawer';
import useFilter from './useFilter';
import useDevicePages from './useDevicePages';
import useMarkers from './useMarkers';
import useCounts from './useCounts';
import MainToolbar from './MainToolbar';
import MainMap from './MainMap';
import { useAttributePreference } from '../common/util/preferences';
import {
  buildFilterParams, buildFilterQuery, fetchDeviceRank, fetchDevicesById, fetchLatestPosition, fetchPositionsById,
} from '../common/util/deviceData';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      position: 'fixed',
      left: 0,
      top: 0,
      height: `calc(100% - ${theme.spacing(3)})`,
      width: theme.dimensions.drawerWidthDesktop,
      margin: theme.spacing(1.5),
      zIndex: 3,
    },
    [theme.breakpoints.down('md')]: {
      height: '100%',
      width: '100%',
    },
  },
  header: {
    zIndex: 6,
  },
  footer: {
    zIndex: 5,
  },
  middle: {
    flex: 1,
    display: 'grid',
  },
  contentMap: {
    gridArea: '1 / 1',
  },
  contentList: {
    gridArea: '1 / 1',
    zIndex: 4,
  },
}));

// How long a scroll or page change may settle before the rows on screen are
// sent to the socket as the new subscription.
const subscriptionDelayMs = 300;

const MainPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const mapOnSelect = useAttributePreference('mapOnSelect', true);

  const selectedDeviceId = useSelector((state) => state.devices.selectedId);
  const devices = useSelector((state) => state.devices.items);
  const positions = useSelector((state) => state.session.positions);
  const markers = useSelector((state) => state.markers.items);

  const [filter, setFilter] = usePersistedState('filter', {
    statuses: [],
    groups: [],
    conditions: [],
    last30DaysTravel: [],
  });
  const [filterSort, setFilterSort] = usePersistedState('filterSort', '');
  const [filterMap, setFilterMap] = usePersistedState('filterMap', true);
  const [autoZoomOnFilter, setAutoZoomOnFilter] = usePersistedState('autoZoomOnFilter', true);
  const [showTerminated, setShowTerminated] = usePersistedState('showTerminated', false);

  const [devicesOpen, setDevicesOpen] = useState(desktop);
  const [eventsOpen, setEventsOpen] = useState(false);

  const onEventsClick = useCallback(() => setEventsOpen(true), [setEventsOpen]);

  // The search box is controlled locally and pushed to the store after a
  // short pause, so a device search over the whole fleet runs once per word
  // typed rather than once per keystroke. The store value can also change
  // from outside (the URL filter parameter), so it is mirrored back into the
  // box when it differs from what was typed.
  const keyword = useSelector((state) => state.devices.filter) || '';
  const [keywordInput, setKeywordInput] = useState(keyword);

  useEffect(() => {
    if (keyword !== keywordInput.toUpperCase()) {
      setKeywordInput(keyword);
    }
  }, [keyword]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (keywordInput.toUpperCase() !== keyword) {
        dispatch(devicesActions.filter(keywordInput));
      }
    }, 200);
    return () => clearTimeout(timeout);
  }, [keywordInput]);

  const distanceUnit = useAttributePreference('distanceUnit');

  useEffect(() => {
    if (!desktop && mapOnSelect && selectedDeviceId) {
      setDevicesOpen(false);
    }
  }, [desktop, mapOnSelect, selectedDeviceId]);

  // The toolbar filters and the keyword go to the server as one query.
  // The list always carries it; the map, and with it the marker stream,
  // only while the map is set to follow the list, and otherwise shows the
  // whole active fleet.
  const filterParams = useMemo(
    () => buildFilterParams({
      keyword, filter, showTerminated, distanceUnit,
    }),
    [keyword, filter, showTerminated, distanceUnit],
  );
  const filterQuery = buildFilterQuery(filterParams);
  const markersLoaded = useMarkers(filterMap ? filterQuery : '');
  const counts = useCounts(distanceUnit);

  useEffect(() => {
    dispatch(sessionActions.updateSubscription({
      distanceUnit: distanceUnit || null,
      filter: filterMap ? filterParams : null,
    }));
  }, [distanceUnit, filterMap, filterParams]);

  // The list is served sorted; newest-first is the only order that makes
  // sense for last update.
  const sort = filterSort === 'lastUpdate' ? 'lastUpdate' : 'name';
  const order = sort === 'lastUpdate' ? 'desc' : 'asc';
  const {
    rows, devices: loadedDevices, total, loaded, loadPage,
  } = useDevicePages({
    query: filterQuery, sort, order, serverFiltered: filterMap,
  });

  const { filteredPositions } = useFilter({
    loaded: loaded && markersLoaded, keyword, filter, showTerminated, filterMap, markers, autoZoomOnFilter,
  });

  // A selected device is brought into view in the list. When its row is
  // already loaded its index is known; otherwise the server is asked where
  // the device sits under the current sort and filter, and the list scrolls
  // there, which loads the page. A device the filter excludes stays where
  // it is: the card still opens from the map.
  const [scrollTo, setScrollTo] = useState(null);
  useEffect(() => {
    if (!selectedDeviceId || !loaded) {
      return undefined;
    }
    const index = rows.findIndex((device) => device && device.id === selectedDeviceId);
    if (index >= 0) {
      setScrollTo({ index, deviceId: selectedDeviceId });
      return undefined;
    }
    let cancelled = false;
    const locate = async () => {
      try {
        const rank = await fetchDeviceRank({
          id: selectedDeviceId, sort, order, query: filterQuery,
        });
        if (!cancelled && Number.isInteger(rank?.offset) && rank.offset >= 0) {
          setScrollTo({ index: rank.offset, deviceId: selectedDeviceId });
        }
      } catch (error) {
        // Not in the list, or the server cannot say; nothing to scroll to.
      }
    };
    locate();
    return () => {
      cancelled = true;
    };
  }, [selectedDeviceId, loaded, sort, order, filterQuery]);

  // The rows on screen are what the socket streams device and position
  // updates for. Scrolling reports them continuously; the subscription is
  // sent once it settles.
  const [visibleIds, setVisibleIds] = useState([]);
  const visibleKeyRef = useRef('');
  const onVisibleChange = useCallback((ids) => {
    const key = ids.join(',');
    if (key !== visibleKeyRef.current) {
      visibleKeyRef.current = key;
      setVisibleIds(ids);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(sessionActions.updateSubscription({ rows: visibleIds }));
    }, subscriptionDelayMs);
    return () => clearTimeout(timeout);
  }, [visibleIds]);

  // The status card wants the whole device and its full position. A marker
  // click can select a device the list has not loaded, and a row only holds
  // a trimmed position, so both are fetched on selection and the socket is
  // asked to keep them current while the card is open.
  useEffect(() => {
    dispatch(sessionActions.updateSubscription({ full: selectedDeviceId ? [selectedDeviceId] : [] }));
    if (!selectedDeviceId) {
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        if (!devices[selectedDeviceId]) {
          const fetched = await fetchDevicesById([selectedDeviceId]);
          if (!cancelled) {
            dispatch(devicesActions.update(fetched));
          }
        }
        const fetched = await fetchLatestPosition(selectedDeviceId);
        if (!cancelled) {
          dispatch(sessionActions.setPositions(fetched));
        }
      } catch (error) {
        // The card draws what the store already holds.
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedDeviceId]);

  // Rows are only streamed while they are on screen, so one scrolled away
  // and back may be behind. The server resends a row's current device and
  // position in the batch after it is subscribed; this is the fallback for
  // the seconds before that lands. Markers arrive for every device, so a
  // row whose marker is ahead of its position or status is stale and is
  // refetched. Each marker state is refreshed at most once, so a device
  // whose marker never lines up with its row cannot be fetched over and
  // over.
  const refreshedRef = useRef({});
  useEffect(() => {
    const stale = visibleIds.filter((id) => {
      const marker = markers[id];
      if (!marker) {
        return false;
      }
      const position = positions[id];
      const device = devices[id];
      const behind = (!position || marker.id > position.id)
        || (device && device.status !== marker.status);
      const state = `${marker.id}|${marker.status}`;
      return behind && refreshedRef.current[id] !== state;
    });
    if (!stale.length) {
      return undefined;
    }
    const timeout = setTimeout(async () => {
      stale.forEach((id) => {
        refreshedRef.current[id] = `${markers[id].id}|${markers[id].status}`;
      });
      try {
        const [fetchedDevices, fetchedPositions] = await Promise.all([
          fetchDevicesById(stale),
          fetchPositionsById(stale.map((id) => markers[id].id)),
        ]);
        dispatch(devicesActions.update(fetchedDevices));
        dispatch(sessionActions.setPositions(fetchedPositions));
      } catch (error) {
        // The next marker will try again.
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [visibleIds, markers]);

  // Leaving the page leaves the socket on unfiltered markers only.
  useEffect(() => () => {
    dispatch(sessionActions.updateSubscription({ rows: [], full: [], filter: null }));
  }, []);

  const selectedPosition = selectedDeviceId ? positions[selectedDeviceId] : undefined;
  const selectedMarker = selectedDeviceId ? markers[selectedDeviceId] : undefined;

  const retVal = (
    <div className={classes.root}>
      {desktop && (
        <MainMap
          filteredPositions={filteredPositions}
          selectedPosition={selectedPosition}
          selectedMarker={selectedMarker}
          onEventsClick={onEventsClick}
        />
      )}
      <div className={classes.sidebar}>
        <Paper square elevation={3} className={classes.header}>
          <MainToolbar
            filteredDevices={loadedDevices}
            loadedDevices={loadedDevices}
            total={total}
            counts={counts}
            devicesOpen={devicesOpen}
            setDevicesOpen={setDevicesOpen}
            keyword={keywordInput}
            setKeyword={setKeywordInput}
            filter={filter}
            setFilter={setFilter}
            filterSort={filterSort}
            setFilterSort={setFilterSort}
            filterMap={filterMap}
            setFilterMap={setFilterMap}
            showTerminated={showTerminated}
            setShowTerminated={setShowTerminated}
            distanceUnit={distanceUnit}
            autoZoomOnFilter={autoZoomOnFilter}
            setAutoZoomOnFilter={setAutoZoomOnFilter}
          />
        </Paper>
        <div className={classes.middle}>
          {!desktop && (
            <div className={classes.contentMap}>
              <MainMap
                filteredPositions={filteredPositions}
                selectedPosition={selectedPosition}
                selectedMarker={selectedMarker}
                onEventsClick={onEventsClick}
              />
            </div>
          )}
          <Paper square className={classes.contentList} style={devicesOpen ? {} : { visibility: 'hidden' }}>
            <DeviceList
              rows={rows}
              total={total}
              loadPage={loadPage}
              scrollTo={scrollTo}
              onVisibleChange={onVisibleChange}
            />
          </Paper>
        </div>
        {desktop && (
          <div className={classes.footer}>
            <BottomMenu filteredDevices={loadedDevices} />
          </div>
        )}
      </div>
      <EventsDrawer open={eventsOpen} onClose={() => setEventsOpen(false)} />
      {selectedDeviceId && (
        <StatusCard
          deviceId={selectedDeviceId}
          position={selectedPosition}
          onClose={() => dispatch(devicesActions.select(null))}
          desktopPadding={theme.dimensions.drawerWidthDesktop}
        />
      )}
    </div>
  );
  return retVal;
};

export default MainPage;
